import React, { useState, useEffect } from 'react';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import { getWeaponDamageDisplay, getProficiency } from '../../utils/weaponDamage';
import { resolveEquipmentReference, ResolvedEquipmentItem } from '../../utils/equipmentResolver';
import ProficiencyDisplay from '../CharacterSheet/ProficiencyDisplay';
import styles from './AttackReferenceSection.module.css';

export interface AttackReferenceSectionProps {
  enhancedCharacter: EnhancedCharacter;
}

/**
 * AttackReferenceSection - Quick reference for attack data (SECONDARY information)
 *
 * Shows:
 * - Proficiency die for attack rolls
 * - Equipped weapons with damage calculations
 * - Attack modifiers
 *
 * This is SECONDARY information checked multiple times per session but not every turn.
 */
export const AttackReferenceSection: React.FC<AttackReferenceSectionProps> = ({
  enhancedCharacter,
}) => {
  const { character, computedStats } = enhancedCharacter;
  const [equippedWeapons, setEquippedWeapons] = useState<Map<string, ResolvedEquipmentItem>>(
    new Map()
  );

  // Resolve equipped weapons on mount/update
  useEffect(() => {
    if (!character) return;

    const resolveWeapons = async () => {
      const equipmentReferences = character.inventory?.equipmentReferences || [];
      const weaponRefs = equipmentReferences.filter((ref) => ref.equippedCount > 0);

      const resolved = new Map<string, ResolvedEquipmentItem>();
      for (const ref of weaponRefs) {
        try {
          const resolvedItem = await resolveEquipmentReference(ref.itemId);
          if (resolvedItem && resolvedItem.type === 'weapon') {
            resolved.set(ref.itemId, resolvedItem);
          }
        } catch (error) {
          console.error(`Failed to resolve weapon ${ref.itemId}:`, error);
        }
      }

      if (resolved.size > 0) {
        setEquippedWeapons(resolved);
      } else {
        setEquippedWeapons(new Map());
      }
    };

    resolveWeapons();
  }, [character?.inventory?.equipmentReferences]);

  if (!character || !computedStats) {
    return (
      <div className={styles.attackReferenceSection}>
        <div className={styles.attackReferenceSection__loading}>
          Loading attack reference...
        </div>
      </div>
    );
  }

  const proficiency = getProficiency(character);

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <section
      className={styles.attackReferenceSection}
      aria-labelledby="attack-reference-heading"
    >
      <h2 id="attack-reference-heading" className={styles.attackReferenceSection__title}>
        Attack Reference
      </h2>

      <div className={styles.attackReferenceSection__content}>
        {/* Proficiency Display */}
        <div className={styles.attackReferenceSection__proficiency}>
          <ProficiencyDisplay character={character} />
        </div>

        {/* Equipped Weapons Section */}
        <div className={styles.attackReferenceSection__weaponsContainer}>
          <h3 className={styles.attackReferenceSection__subsectionTitle}>
            Equipped Weapons
          </h3>

          {equippedWeapons.size > 0 ? (
            <div className={styles.attackReferenceSection__weaponsGrid}>
              {Array.from(equippedWeapons.values()).map((weapon) => {
                const weaponFeature = (weapon as any).feature || '';
                return (
                  <div
                    key={weapon.id}
                    className={styles.attackReferenceSection__weaponCard}
                  >
                    <div className={styles.attackReferenceSection__weaponHeader}>
                      <span className={styles.attackReferenceSection__weaponName}>
                        {capitalizeFirstLetter(weapon.name)}
                      </span>
                    </div>
                    <div className={styles.attackReferenceSection__weaponStats}>
                      <span className={styles.attackReferenceSection__weaponDamage}>
                        {getWeaponDamageDisplay(weapon, proficiency)}
                      </span>
                      {weaponFeature && (
                        <>
                          <span className={styles.attackReferenceSection__weaponSeparator}>-</span>
                          <span className={styles.attackReferenceSection__weaponFeature}>
                            {weaponFeature}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.attackReferenceSection__emptyState}>
              <div className={styles.attackReferenceSection__emptyIcon}>🗡️</div>
              <div className={styles.attackReferenceSection__emptyText}>
                No weapons equipped
              </div>
              <div className={styles.attackReferenceSection__emptyHint}>
                Equip weapons in your inventory to see them here
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AttackReferenceSection;
